import SideModal from '../common/modals/SideModal';
import SideModalHeader from '../common/modals/SideModal/SideModalHeader';
import clsx from 'clsx';
import { getRouteFromDeepLink, groupByCreationDate } from 'src/libs/utils';
import Link from 'next/link';
import { markNotificationAsClicked } from 'src/libs/http-api';
export enum NotificationStatus {
    CREATED,
    SEEN,
    CLICKED,
}

export enum NotificationTopic {
    KYC_STATUS = 'KYC_STATUS',
}

export type TNotification = {
    id: string;
    userId: string;
    topic: NotificationTopic;
    status: NotificationStatus;
    message: string;
    seenAt: Date | null;
    createdAt: Date;
    updatedAt: Date | null;
    deletedAt: Date | null;
    firstClickAt: Date | null;
    deepLink: string;
};

const NotificationItemImage = ({}) => (
    <div className="w-[38px] h-[38px] min-w-[38px] max-w-[38px] min-h-[38px] max-h-[38px] flex-1 bg-grayColor rounded-full">
        &nbsp;
    </div>
);
const NotificationItemMessage = ({
    message,
    status,
}: {
    message: string;
    status: NotificationStatus;
}) => (
    <div
        className={clsx(
            'font-mont text-sm',
            status === NotificationStatus.CREATED && 'font-semibold',
        )}
    >
        {message}
    </div>
);
const NotificationItemTime = ({
    createdAt,
    status,
}: {
    createdAt: Date;
    status: NotificationStatus;
}) => (
    <div
        className={clsx(
            'font-mont text-sm',
            status === NotificationStatus.CREATED && 'font-semibold',
        )}
    >
        {createdAt.toLocaleString()}
    </div>
);
const NotificationItemStatus = ({ status }: { status: NotificationStatus }) =>
    Number(status) === NotificationStatus.CREATED ? (
        <div className="w-[12px] h-[12px] min-w-[12px] max-w-[12px] min-h-[12px] max-h-[12px] flex-1 bg-redColor rounded-full"></div>
    ) : null;

const NotificationSubtitle = ({ children }: { children: string }) => (
    <h2 className="mt-[10px] mb-[6px] font-mont font-semibold text-grayText">
        {children}
    </h2>
);

const NotificationItem = (props: {
    notification: TNotification;
    onClose: () => void;
}) => {
    const onClick = () => {
        props.onClose();
    };
    return (
        <Link passHref href={getRouteFromDeepLink(props.notification.deepLink)}>
            <div
                onClick={onClick}
                className=" cursor-pointer flex justify-between items-center hover:bg-hoverBg"
            >
                <NotificationItemImage />
                <div
                    className={clsx('p-[12px] flex-1', {
                        'font-semibold':
                            NotificationStatus.CREATED ===
                            Number(props.notification?.status),
                    })}
                >
                    <NotificationItemMessage {...props.notification} />
                    <NotificationItemTime {...props.notification} />
                </div>
                <NotificationItemStatus status={props.notification?.status} />
            </div>
        </Link>
    );
};

const NotificationSection = (props: {
    notifications: TNotification[];
    onClose: () => void;
}) => {
    if (props.notifications.length === 0)
        return <div>You don&apos;t have any notifications yet</div>;

    const groupedNotifications = groupByCreationDate(props.notifications);
    const el = Object.entries(groupedNotifications).map(
        ([group, notifications]) => (
            <>
                <NotificationSubtitle key={group}>{group}</NotificationSubtitle>
                {notifications.map((notification) => (
                    <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onClose={props.onClose}
                    />
                ))}
            </>
        ),
    );
    return <>{el}</>;
};

const demoNotifications = [
    {
        id: '1',
        userId: '1',
        topic: NotificationTopic.KYC_STATUS,
        status: NotificationStatus.CREATED,
        message: 'Your KYC status is pending',
        seenAt: null,
        createdAt: new Date(),
        updatedAt: new Date(),
        deletedAt: new Date(),
        firstClickAt: new Date(),
    },
    {
        id: '2',
        userId: '1',
        topic: NotificationTopic.KYC_STATUS,
        status: NotificationStatus.SEEN,
        message: 'Your KYC status is pending',
        seenAt: null,
        createdAt: new Date(),
        updatedAt: new Date(),
        deletedAt: new Date(),
        firstClickAt: new Date(),
    },
    {
        id: '2',
        userId: '1',
        topic: NotificationTopic.KYC_STATUS,
        status: NotificationStatus.SEEN,
        message: 'Your KYC status is pending',
        seenAt: null,
        createdAt: new Date(2022, 6, 29),
        updatedAt: new Date(),
        deletedAt: new Date(),
        firstClickAt: new Date(),
    },
    {
        id: '2',
        userId: '1',
        topic: NotificationTopic.KYC_STATUS,
        status: NotificationStatus.SEEN,
        message: 'Your KYC status is pending',
        seenAt: null,
        createdAt: new Date(2022, 6, 29),
        updatedAt: new Date(),
        deletedAt: new Date(),
        firstClickAt: new Date(),
    },
    {
        id: '2',
        userId: '1',
        topic: NotificationTopic.KYC_STATUS,
        status: NotificationStatus.SEEN,
        message: 'Your KYC status is pending',
        seenAt: null,
        createdAt: new Date(2022, 6, 28),
        updatedAt: new Date(),
        deletedAt: new Date(),
        firstClickAt: new Date(),
    },
] as TNotification[];

export const NotificationModal = ({
    open,
    onClose,
    notifications = [],
}: {
    open: boolean;
    onClose: () => any;
    notifications: TNotification[];
}) => {
    return (
        <SideModal open={open} onClose={onClose} headerLabel="Notifications">
            <NotificationSection
                notifications={notifications}
                onClose={onClose}
            />
        </SideModal>
    );
};
